//@ts-check
import React, { useRef, useState } from "react";
import { useAppDispatch } from "src/utils/ReduxHooks";
import UserImg from "components/UserImg";
import { endPoint } from "src/AppConstants";
import FileIcon from "components/FileIcons/FileIcon";
import { useI18n } from "components/lni18n";
import LnIcon from "components/LnIcon";
import { ModalDialog } from "components/OnlineModal";
import WaitRipple from "components/WaitRipple";
import useTypedSelector from 'utils/useTypedSelector';
import { forumService } from './ForumDuck';
import { Confirm, ModalConstants } from "components/OnlineModal";
import ForumAnswerCard from "./ForumAnswerCard";
import ForumForm from "./ForumForm"
import classes from './forum.module.scss';
import ShowImage from "./ShowImage";
import EditForumForm from "./EditForumForm";
import LnDropDownMenu from "components/LnDropDownMenu";
import { DropdownItem, DropdownMenu } from "reactstrap";
import { useEffect } from "react";
import ForumTestCommentStub from "./ForumTestCommentStub";
import { useCallback } from "react";
import { Alert } from "reactstrap/lib";

const ForumThread = ({ onLeaveThreadClick }) => {
    /** @type {import('redux').Dispatch<any>} */
    const dispatch = useAppDispatch();
    const { languageService: t } = useI18n();
    const [editMode, setEditMode] = useState(false);
    const [showCommentDialog, setShowCommentDialog] = useState(false);
    const [commentIsPostedOk, setCommentPosted] = useState(false);



    const toggleEdit = () => {
        setEditMode(old => !old);
    };

    /**
     * @type {{currentThread: import('./Forum').ForumThreadDto, currentForum: import('./Forum').ForumPageDto, userId: string, inLectureDialog: boolean }}
     */
    const { currentThread, currentForum, userId, inLectureDialog } = useTypedSelector(state => state.forum);
    const { eventsSinceDate } = useTypedSelector(state => state.classData);
    const currentUser = useTypedSelector(state => state.login.user);
    const [showForm, toggleShowForm] = useState(false);


    const teacherFeatures = useRef(null);

    const onDismissCommentDialog = useCallback(() => {
        setShowCommentDialog(false);
        if (teacherFeatures.current) {
            dispatch(teacherFeatures.current.TestCommentService.setCurrentTestInComment(null));
        }
    }, [teacherFeatures, dispatch]);


    let openCommentDialog = useCallback((id) => {
        if (teacherFeatures.current) {
            teacherFeatures.current.TestResultService
                .GetTestResultForComment({ resultId: id, classId: currentForum.Id, dispatch });
        }
        setShowCommentDialog(true);
    }, [currentForum, dispatch, teacherFeatures]);

    useEffect(()=>{
        toggleShowForm(inLectureDialog);
    },[inLectureDialog])

    useEffect(() => {
        if (currentForum && currentForum.IsTeacher) {
            import("src/teacherFeatures").then(features => {
                teacherFeatures.current = features;
            });
        }
    }, [currentForum]);

    /**
     * @type {{current: HTMLDivElement}}
     */
    const articleDiv = useRef(null);


    const getArticleText = () => {
        if (articleDiv.current) {
            return articleDiv.current.innerText;
        }

        return null;
    }


    const toggleForm = () => {
        toggleShowForm(old => !old);
    };

    if (currentForum === null || currentThread === null) {
        return <WaitRipple />
    };

    const setPublished = () => {
        dispatch(forumService.setPostingPublishState({ forumId: currentForum.Id, threadId: currentThread.Id, parentId: null, state: !currentThread.Published }));
    };


    const editPost = () => {
        setEditMode(true);
    }

    const deleteCurrent = async () => {
        let mess = !currentThread.ConnectedTest ? t.getText("delete_posting") :
            t.getText("forum.delete.post.warning.testresults.are.deleted");


        const result = await Confirm({
            buttons: [],
            backdrop: 'static',
            class: null,
            title: t.getText("delete"),
            message: (<div className="preserve-white">{mess}</div>),
            languageService: t,
            type: "yes/no"
        });

        if (result === ModalConstants.Yes) {
            await dispatch(forumService.deletePosting(currentForum.Id, currentThread.Id, undefined));
            onLeaveThreadClick();

        };
    };

    const displayReadBy = () => {
        ModalDialog({
            bodyClass: 'modal-body',
            buttons: [{
                className: 'btn btn-primary btn-small',
                text: t.getText('close'),
                type: 'dismiss'
            }],
            headerClass: 'modal-header-default',
            message: (<>
                <table role="table" className={["table", classes['forum-table']].join(' ')}>
                    <thead>
                        <tr role="row">
                            <th role="columnheader" className={classes['th']}>
                                {t.getText('name')}
                            </th>
                            <th role="columnheader" className={classes['th']}>
                                {t.getText('date_and_time')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentThread.ReadBy.map(tr => (
                            (
                                <tr role="row" key={tr.Id}>
                                    <td role="cell">
                                        <span className="d-flex flex-row align-items-center">
                                            {tr.ImageUrl ? <UserImg src={endPoint.API_HOST + tr.ImageUrl} className={['rounded-circle mr-4', 'avatar-img', classes['thread-writer-avatar']].join(' ')} alt={tr.FullName} /> : <LnIcon className={['rounded-circle mr-4', 'avatar-img', classes['thread-generic-avatar']].join(' ')} name="user-icon" />}
                                            {tr.FullName}
                                        </span>
                                    </td>
                                    <td role="cell">
                                        {t.getShortDateTimeString(tr.LastRead)}
                                    </td>
                                </tr>
                            )
                        ))}
                    </tbody>

                </table>
            </>),
            title: t.getText('read_by')
        });
    }

    const onPostCreated = () => {
        if (inLectureDialog) {
            setCommentPosted(true);
        }
    }

    const thisPostIsNew = currentThread.Created !==null &&  currentThread.Writer.Id !== currentUser.Id && eventsSinceDate.getTime() < currentThread.Created.getTime();

    return (
        <>
            <div className="card-header ln-nostyle d-flex justify-content-between">
                <div className="d-flex align-items-center">
                    <h3>
                        <LnIcon className="header-icon" name="forum-icon" /></h3>
                    <h3>
                        {t.getText('forum')} / {currentThread.Header}
                    </h3>

                </div>
                {!inLectureDialog &&
                    <div className="d-print-none">
                        <button type="button" className="btn btn-inverse btn-small mr-4 mb-3" onClick={() => displayReadBy()}>{t.getText('read_by')}</button>
                        <button id="backfromForumThread" type="button" role="link" className="btn btn-primary btn-small" onClick={() => onLeaveThreadClick()} >{t.getText("back")}</button>
                    </div>
                }
                {/* else */}
                {inLectureDialog &&
                    <div className="d-print-none">
                        <button type="button" role="link" className="btn btn-primary btn-small" onClick={() => onLeaveThreadClick(commentIsPostedOk)} >
                            {commentIsPostedOk ? t.getText("continue") : t.getText("return")}
                        </button>
                    </div>
                }
            </div>

            <div className="card-body pt-5 ">


                <div className={["position-relative card mb-5", classes['forum-card']].join(' ')}>

                    <div className={"d-flex justify-content-between pr-5" + (currentThread.Writer.Id === userId ? " bg-primary-lightest" : "")}>

                        <div className={"avatar-wrapper"}>
                            {currentThread.Writer.ImageUrl ? <UserImg src={endPoint.API_HOST + currentThread.Writer.ImageUrl} className={['rounded-circle m-4', 'avatar-img'].join(' ')} alt={currentThread.Writer.FullName} /> : <LnIcon className={['rounded-circle m-4', 'avatar-img'].join(' ')} name="user-icon" />}
                        </div>
                        <div className="flex-grow-1 pl-4">
                            <div className={['card-title d-flex justify-content-between mt-4', classes['forum-head']].join(' ')}>
                                <h5 className={classes['forum-title']}>
                                    {currentThread.Writer.FullName}
                                </h5>
                                <div className="d-flex">
                                    <small className={"d-flex flex-row flex-sm-column align-items-end " + (thisPostIsNew && " font-weight-bold")}>
                                        <div>
                                            {t.getShortDateTimeString(currentThread.Created)}
                                        </div>
                                        {currentThread.Edited && <>
                                            <div>{t.getText("edited")} </div>
                                            <div>{t.getShortDateTimeString(currentThread.Edited)}</div></>}
                                    </small>
                                    <div className="d-flex flex-md-column  flex-row">
                                        {!inLectureDialog && (currentThread.IsModerator || currentThread.CanEdit) &&
                                            <LnDropDownMenu
                                                direction="down"
                                                caret={false}
                                                toggleComponent={
                                                    <button type="button" className="edit round" title={t.getText("edit")} >
                                                        <LnIcon className="icon-small" name="edit-icon" alt="Edit" />
                                                    </button>
                                                }
                                                toogleClassName=""
                                                className="ml-3 mb-3"
                                                toogleTagName="div"
                                            >
                                                <DropdownMenu right className="" container="body">
                                                    <div className="d-flex flex-column">
                                                        {currentThread.CanEdit &&
                                                            <DropdownItem onClick={() => editPost()} tag="span" >{t.getText("edit")}</DropdownItem>
                                                        }
                                                        {(currentThread.IsModerator || currentThread.CanDelete) &&
                                                            <DropdownItem onClick={() => deleteCurrent()} tag="span" >{t.getText("delete")}</DropdownItem>
                                                        }

                                                    </div>
                                                </DropdownMenu>
                                            </LnDropDownMenu>
                                        }
                                        {!inLectureDialog && currentThread.IsModerator &&
                                            <button type="button" onClick={setPublished} className={"ml-3 edit round " + (currentThread.Published ? "" : "inverse")}>
                                                <LnIcon className="small" name={currentThread.Published ? "eye-icon" : "eye-icon-off"} />
                                            </button>

                                        }
                                    </div>

                                </div>

                            </div>
                            <div className={[classes['forum-text-wrapper']].join(' ')}>
                                <div className="d-flex flex-wrap flex-md-nowrap">
                                    {(currentThread.Attachment)
                                        && <div className="mr-4">
                                            {
                                                currentThread.Attachment.MediaType === 1 ?
                                                    <button type="button" className="btn btn-link" onClick={() => ShowImage(endPoint.API_HOST + currentThread.Attachment.Url, currentThread.Attachment.FileName, t)}>
                                                        <img src={endPoint.API_HOST + currentThread.Attachment.ThumbUrl} alt={currentThread.Attachment.FileName} />
                                                    </button>
                                                    : <a href={endPoint.API_HOST + currentThread.Attachment.Url}>
                                                        <FileIcon className={[classes['file-icon']].join(' ')} size="512px" name={(currentThread.Attachment.FileName || '').split('.').pop()} /><br />
                                                        {currentThread.Attachment.FileName}
                                                    </a>
                                            }
                                        </div>}
                                    <div className="mb-3 flex-grow-1">
                                        {!editMode &&
                                            <>
                                                <div ref={articleDiv} className={"word-break-word infocard-text max-col-width " + (thisPostIsNew && " first-line-bold")} dangerouslySetInnerHTML={{ __html: currentThread.Article }}></div>
                                                {currentThread.TestData &&
                                                    <div className="badge-pill badge-warning badge">{t.getText("forum.part.of.test", currentThread.TestData.TestName, currentThread.TestData.PartName)}</div>
                                                }

                                                {(currentThread.Attachment && currentThread.Attachment.MediaType === 3) &&
                                                    <audio controls={true} src={endPoint.API_HOST + currentThread.Attachment.Url}></audio>
                                                }

                                                {(currentThread.Attachment && currentThread.Attachment.MediaType === 2) &&
                                                    <video className="mt-3" controls={true} src={endPoint.API_HOST + currentThread.Attachment.Url} ></video>
                                                }
                                            </>
                                        }

                                        {editMode &&
                                            <EditForumForm onLeaveThread={() => onLeaveThreadClick(commentIsPostedOk)} forumId={currentForum.Id} parentId={undefined} postType="thread" fileOptions={{
                                                imageFileTypes: currentForum.ImageFileTypes,
                                                videoFileTypes: currentForum.VideoFileTypes,
                                                audioFileTypes: currentForum.AudioFileTypes,
                                                AcceptFileTypes: currentForum.AcceptFileTypes,
                                                MaxFileSize: currentForum.MaxFileSize

                                            }} attachment={currentThread.Attachment}
                                                article={getArticleText()}
                                                showFileUpload={true}
                                                postid={currentThread.Id}
                                                toggleForm={toggleEdit} />
                                        }

                                    </div>
                                </div>
                            </div>


                        </div>
                    </div>
                    {!commentIsPostedOk && currentThread.CanAddAnwser && !showForm && currentThread.Published ? <div className="mt-3 pl-4 mb-3">
                        <button type="button" className="btn btn-primary btn-small" onClick={() => toggleForm()}>{t.getText('comment')} </button></div> : null}
                    {showForm ? <div><ForumForm hideCancel={inLectureDialog} onPostCreated={onPostCreated} fileOptions={{
                        imageFileTypes: currentForum.ImageFileTypes,
                        videoFileTypes: currentForum.VideoFileTypes,
                        audioFileTypes: currentForum.AudioFileTypes,
                        AcceptFileTypes: currentForum.AcceptFileTypes,
                        MaxFileSize: currentForum.MaxFileSize
                    }} formType={'answer'} isTeacher={currentForum.IsTeacher} replyTo={currentThread.Id} forumId={currentThread.ForumId} toggleForm={toggleForm} /></div> : null}

                    {inLectureDialog && commentIsPostedOk && <Alert color="primary" className="mx-4 preserve-white">{t.getText("forum.post.ready.in.lecture")}
                        </Alert>}

                    {currentThread.Answers.length ? <div className="mt-3">
                        {currentThread.Answers.map(a => (
                            <ForumAnswerCard readonly={inLectureDialog} onCommentTest={openCommentDialog} isTeacher={currentForum.IsTeacher} key={a.Id} answer={a} />
                        ))}
                    </div> : <div></div>}


                </div>

            </div>
            {currentForum && currentForum.IsTeacher && <ForumTestCommentStub show={showCommentDialog} onDismiss={() => onDismissCommentDialog()} />}
        </>
    )

}


export default ForumThread;